import { Button, Checkbox, Col, Form, Input, Row } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { NotificationContext } from '../../providers/NotificationProvider';
import { requestInformation } from '../../features/user/userSlice';
import logo from '../../assets/images/logo.png';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../i18n';
import LanguageTabs from '../shared/LanguageTabs';
import { useTurnstileCaptcha } from './useTurnstileCaptcha';
import { get } from '../../api/base';
import { VirtualCourseInterestOptionType } from '../../types/types';

type InformationFormFields = {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  interest_topic?: string;
  virtual_course_interests?: string[];
};

const RequestInformationForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const source = (searchParams.get('source') || '').trim();
  const lang = (searchParams.get('lang') || '').trim();
  const { captchaRef, captchaToken, captchaReady, resetCaptcha, turnstileSiteKey } = useTurnstileCaptcha();
  const [courseOptions, setCourseOptions] = useState<VirtualCourseInterestOptionType[]>([]);

  useEffect(() => {
    if (!lang) {
      return;
    }

    setLanguage(lang);
  }, [lang]);

  useEffect(() => {
    get('public/request-information/settings/')
      .then((response: any) => {
        setCourseOptions(response?.data?.virtual_course_interest_options || []);
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('No se pudieron cargar las opciones de cursos virtuales.'), 'bottomRight');
      });
  }, [t, triggerNotification]);

  const handleSubmit = (values: InformationFormFields) => {
    if (!turnstileSiteKey) {
      triggerNotification('error', t('Error'), t('El captcha no está configurado.'), 'bottomRight');
      return;
    }

    if (!captchaToken) {
      triggerNotification('error', t('Error'), t('Por favor valida el captcha.'), 'bottomRight');
      return;
    }

    dispatch(
      requestInformation({
        name: values.name || '',
        email: values.email || '',
        phone: values.phone || '',
        company: values.company || '',
        interest_topic: values.interest_topic || '',
        virtual_course_interests: values.virtual_course_interests || [],
        source,
        captcha_token: captchaToken,
      }) as any
    )
      .then((response: any) => {
        if (requestInformation.fulfilled.match(response)) {
          form.resetFields();
          resetCaptcha();
          triggerNotification('success', t('Éxito'), response.payload.message, 'bottomRight');
          return;
        }

        resetCaptcha();
        triggerNotification('error', t('Error'), response.payload?.message || t('No se pudo enviar la solicitud.'), 'bottomRight');
      })
      .catch((err: any) => {
        resetCaptcha();
        triggerNotification('error', t('Error'), err.message, 'bottomRight');
      });
  };

  return (
    <div className='trial-request-page'>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageTabs compact />
      </div>
      <Row className='trial-request-layout'>
        <Col className='trial-request-panel'>
          <img src={logo} alt='logo' className='logo' />
          <div className='trial-request-copy'>
            <h1>{t('Solicitar Información')}</h1>
            <p>{t('Déjanos tus datos y tu tema de interés para que podamos contactarte.')}</p>
          </div>

          <Form form={form} name='request-information' layout='vertical' onFinish={handleSubmit} autoComplete='off'>
            <Form.Item<InformationFormFields>
              label={t('Nombre')}
              name='name'
              rules={[{ required: true, message: t('Por favor ingresa tu nombre.') }]}
            >
              <Input />
            </Form.Item>

            <Form.Item<InformationFormFields>
              label={t('Correo electrónico')}
              name='email'
              rules={[
                { required: true, message: t('Por favor ingresa tu correo electrónico.') },
                { type: 'email', message: t('Por favor ingresa un correo electrónico válido.') },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item<InformationFormFields>
              label={t('Empresa')}
              name='company'
            >
              <Input />
            </Form.Item>

            <Form.Item<InformationFormFields>
              label={t('Teléfono')}
              name='phone'
              rules={[
                { required: true, message: t('Por favor ingresa tu teléfono.') },
                {
                  validator: async (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    const digits = String(value).replace(/\D/g, '');
                    if (digits.length < 7) {
                      return Promise.reject(new Error(t('El teléfono debe tener al menos 7 dígitos.')));
                    }
                    if (digits.length > 15) {
                      return Promise.reject(new Error(t('El teléfono no puede tener más de 15 dígitos.')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              normalize={(value) => (value ? String(value).replace(/\D/g, '') : value)}
            >
              <Input />
            </Form.Item>

            <Form.Item<InformationFormFields>
              label={t('Curso Virtual de Interés')}
              name='virtual_course_interests'
            >
              <Checkbox.Group
                options={courseOptions.map((option) => ({
                  label: option.name,
                  value: option.name,
                }))}
              />
            </Form.Item>

            <Form.Item<InformationFormFields>
              label={t('Tema de interés')}
              name='interest_topic'
            >
              <Input.TextArea rows={5} />
            </Form.Item>

            <div className='trial-captcha-wrapper'>
              {turnstileSiteKey ? (
                <div ref={captchaRef} />
              ) : (
                <small>{t('Configura REACT_APP_TURNSTILE_SITE_KEY para habilitar el captcha.')}</small>
              )}
            </div>

            <div className='trial-request-actions'>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type='primary' htmlType='submit' disabled={!captchaReady && !!turnstileSiteKey}>
                  {t('Enviar solicitud')}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default RequestInformationForm;
